"use client";

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Section from '@/components/Section';
import { useState, useEffect } from 'react';

const TechnologySafetyPage = () => {
  // State to track if component is mounted
  const [isMounted, setIsMounted] = useState(false);

  // Set mounted state after component loads
  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <>
      {/* Hero Section with Tower reference styling */}
      <section 
        className="relative py-20 sm:py-24 md:py-28 px-4 bg-gradient-to-br from-[#1a2942] via-[#234563] to-[#1e5e52] text-white overflow-hidden min-h-[460px] !min-h-[460px] sm:min-h-0"
        style={{ minHeight: '460px' }}
      >
        {/* Abstract shapes with optimized animations */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-1/4 w-60 sm:w-80 md:w-96 h-60 sm:h-80 md:h-96 rounded-full bg-[#38b5a0] blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-1/5 w-60 sm:w-70 md:w-80 h-60 sm:h-70 md:h-80 rounded-full bg-[#f8c156] blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-40 left-1/3 w-40 sm:w-50 md:w-60 h-40 sm:h-50 md:h-60 rounded-full bg-[#38b5a0]/40 blur-3xl animate-pulse" style={{animationDelay: '1.5s'}}></div>
        </div>
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <span 
            className="inline-block bg-[#38b5a0]/20 text-white text-lg mb-4 py-1 px-4 rounded-full border border-[#38b5a0]/30 animate-[fadeSlideUp_0.7s_0.2s_forwards] will-change-transform will-change-opacity" 
            style={{opacity: 0}}
          >
            Advanced EMS Technology
          </span>
          <h1 
            className="text-3xl md:text-5xl font-bold mb-6 leading-tight animate-[fadeSlideUp_0.7s_0.4s_forwards] will-change-transform will-change-opacity" 
            style={{opacity: 0}}
          >
            Technology & Safety Standards
          </h1>
          <p 
            className="text-xl opacity-90 max-w-3xl mx-auto animate-[fadeSlideUp_0.7s_0.6s_forwards] will-change-transform will-change-opacity" 
            style={{opacity: 0}}
          >
            Experience cutting-edge EMS technology built with the highest safety standards and backed by scientific research.
          </p>
        </div>
        
        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden">
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="w-full h-[60px]"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="fill-white"
            ></path>
          </svg>
        </div>
      </section>

      {/* Define the animations so they're available in this component */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeSlideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Technology Overview */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-[#f4fbfa]">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            <div className="md:w-1/2">
              <span className="inline-block bg-[#38b5a0]/10 text-[#38b5a0] text-sm font-semibold mb-3 py-1 px-3 rounded-full">
                CUTTING-EDGE TECHNOLOGY
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-5 text-[#1a2942]">EMS Technology That Sets the Standard</h2>
              <p className="text-[#455a73] mb-5 text-lg">
                The I-motion EMS system represents the pinnacle of whole-body electrical muscle stimulation technology. Our systems are designed with precision control, user comfort, and maximum effectiveness at their core.
              </p>
              <p className="text-[#455a73] mb-6 text-lg">
                Every component of our systems undergoes rigorous testing and quality control to ensure consistent, safe, and effective performance in both personal training and rehabilitation settings.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link href="/contact" className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[#38b5a0] text-white font-medium hover:bg-[#2da08c] transition-colors">
                  Schedule a Demo
                </Link>
                <Link href="/roi" className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[#f8c156] text-[#1a2942] font-medium hover:bg-[#f0b33d] transition-colors">
                  View ROI Analysis
                </Link>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="relative rounded-xl overflow-hidden shadow-2xl">
                <Image 
                  src="/images/ems-suit-circuit-board.png" 
                  alt="Advanced EMS suit Technology" 
                  className="w-full h-auto object-cover"
                  width={600}
                  height={400}
                  priority
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a2942]/70 to-transparent flex items-end">
                  <div className="p-6">
                    <span className="bg-[#38b5a0] text-white text-xs font-medium px-2.5 py-1 rounded-full">CE CERTIFIED</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block bg-[#38b5a0]/10 text-[#38b5a0] text-sm font-semibold mb-3 py-1 px-3 rounded-full">
              KEY FEATURES
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#1a2942]">Advanced System Features</h2>
            <p className="text-[#455a73] max-w-3xl mx-auto text-lg">
              Our I-motion systems combine advanced technology with user-friendly interfaces to deliver optimal results for every client
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="group bg-white p-8 rounded-xl shadow-md border border-[#e8f5f2] hover:shadow-xl transition-all duration-300 hover:border-[#38b5a0]/30 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#38b5a0]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-[#38b5a0]/10 flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <svg className="w-8 h-8 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4 text-[#1a2942] group-hover:text-[#38b5a0] transition-colors duration-300">Precision Impulse Control</h3>
                <p className="text-[#455a73] leading-relaxed">
                  Multi-channel technology allows for individual control of up to 12 muscle groups with precise impulse adjustment.
                </p>
              </div>
            </div>
            
            {/* Feature 2 */}
            <div className="group bg-white p-8 rounded-xl shadow-md border border-[#e8f5f2] hover:shadow-xl transition-all duration-300 hover:border-[#f8c156]/30 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#f8c156]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-[#f8c156]/10 flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <svg className="w-8 h-8 text-[#f8c156]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4 text-[#1a2942] group-hover:text-[#f8c156] transition-colors duration-300">Wireless Connectivity</h3>
                <p className="text-[#455a73] leading-relaxed">
                  Bluetooth-enabled system for wireless control via tablet, providing mobility and ease of use.
                </p>
              </div>
            </div>
            
            {/* Feature 3 */}
            <div className="group bg-white p-8 rounded-xl shadow-md border border-[#e8f5f2] hover:shadow-xl transition-all duration-300 hover:border-[#2389db]/30 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#2389db]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-[#2389db]/10 flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <svg className="w-8 h-8 text-[#2389db]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4 text-[#1a2942] group-hover:text-[#2389db] transition-colors duration-300">Customizable Programs</h3>
                <p className="text-[#455a73] leading-relaxed">
                  Store and modify training programs based on client goals, fitness levels, and progress tracking.
                </p>
              </div>
            </div>
            
            {/* Feature 4 */}
            <div className="group bg-white p-8 rounded-xl shadow-md border border-[#e8f5f2] hover:shadow-xl transition-all duration-300 hover:border-[#e05c88]/30 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#e05c88]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-[#e05c88]/10 flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <svg className="w-8 h-8 text-[#e05c88]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4 text-[#1a2942] group-hover:text-[#e05c88] transition-colors duration-300">Client Management</h3>
                <p className="text-[#455a73] leading-relaxed">
                  Integrated client database for storing profiles, session history, and personalized settings.
                </p>
              </div>
            </div>
            
            {/* Feature 5 */}
            <div className="group bg-white p-8 rounded-xl shadow-md border border-[#e8f5f2] hover:shadow-xl transition-all duration-300 hover:border-[#38b5a0]/30 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#38b5a0]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-[#38b5a0]/10 flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <svg className="w-8 h-8 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4 text-[#1a2942] group-hover:text-[#38b5a0] transition-colors duration-300">Advanced Safety Features</h3>
                <p className="text-[#455a73] leading-relaxed">
                  Built-in safety mechanisms including emergency stop, automatic intensity regulation, and system monitoring.
                </p>
              </div>
            </div>
            
            {/* Feature 6 */}
            <div className="group bg-white p-8 rounded-xl shadow-md border border-[#e8f5f2] hover:shadow-xl transition-all duration-300 hover:border-[#f8c156]/30 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#f8c156]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-[#f8c156]/10 flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <svg className="w-8 h-8 text-[#f8c156]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4 text-[#1a2942] group-hover:text-[#f8c156] transition-colors duration-300">Performance Analytics</h3>
                <p className="text-[#455a73] leading-relaxed">
                  Track and visualize client progress with comprehensive analytics and reporting tools.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety Standards */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#f4fbfa] to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block bg-[#38b5a0]/10 text-[#38b5a0] text-sm font-semibold mb-3 py-1 px-3 rounded-full">
              SAFETY FIRST
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#1a2942]">Built to the Highest Safety Standards</h2>
            <p className="text-[#455a73] max-w-3xl mx-auto text-lg">
              Safety is our top priority, with every system designed and certified to meet rigorous international standards
            </p>
        </div>
        
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="group bg-white rounded-xl shadow-md p-8 border border-[#e8f5f2] hover:shadow-xl transition-all duration-300 hover:border-[#38b5a0]/30 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#38b5a0]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-[#38b5a0]/10 flex items-center justify-center mr-4 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                    <svg className="w-8 h-8 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2942] group-hover:text-[#38b5a0] transition-colors duration-300">Certifications & Compliance</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start p-4 bg-[#38b5a0]/5 rounded-lg group-hover:bg-[#38b5a0]/10 transition-colors duration-300">
                    <span className="flex-shrink-0 w-8 h-8 bg-[#38b5a0]/20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </span>
                    <span className="text-[#455a73] leading-relaxed">CE certified medical technology</span>
                  </li>
                  <li className="flex items-start p-4 bg-[#38b5a0]/5 rounded-lg group-hover:bg-[#38b5a0]/10 transition-colors duration-300">
                    <span className="flex-shrink-0 w-8 h-8 bg-[#38b5a0]/20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </span>
                    <span className="text-[#455a73] leading-relaxed">ISO 13485 quality management system</span>
                  </li>
                  <li className="flex items-start p-4 bg-[#38b5a0]/5 rounded-lg group-hover:bg-[#38b5a0]/10 transition-colors duration-300">
                    <span className="flex-shrink-0 w-8 h-8 bg-[#38b5a0]/20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </span>
                    <span className="text-[#455a73] leading-relaxed">Medical Device Directive 93/42/EEC compliance</span>
                  </li>
                  <li className="flex items-start p-4 bg-[#38b5a0]/5 rounded-lg group-hover:bg-[#38b5a0]/10 transition-colors duration-300">
                    <span className="flex-shrink-0 w-8 h-8 bg-[#38b5a0]/20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </span>
                    <span className="text-[#455a73] leading-relaxed">Regular third-party safety audits</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="group bg-white rounded-xl shadow-md p-8 border border-[#e8f5f2] hover:shadow-xl transition-all duration-300 hover:border-[#f8c156]/30 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#f8c156]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-[#f8c156]/10 flex items-center justify-center mr-4 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                    <svg className="w-8 h-8 text-[#f8c156]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-[#1a2942] group-hover:text-[#f8c156] transition-colors duration-300">Built-in Safety Mechanisms</h3>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start p-4 bg-[#f8c156]/5 rounded-lg group-hover:bg-[#f8c156]/10 transition-colors duration-300">
                    <span className="flex-shrink-0 w-8 h-8 bg-[#f8c156]/20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-[#f8c156]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </span>
                    <span className="text-[#455a73] leading-relaxed">Emergency stop function accessible to both trainer and client</span>
                  </li>
                  <li className="flex items-start p-4 bg-[#f8c156]/5 rounded-lg group-hover:bg-[#f8c156]/10 transition-colors duration-300">
                    <span className="flex-shrink-0 w-8 h-8 bg-[#f8c156]/20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-[#f8c156]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </span>
                    <span className="text-[#455a73] leading-relaxed">Automatic intensity modulation based on user feedback</span>
                  </li>
                  <li className="flex items-start p-4 bg-[#f8c156]/5 rounded-lg group-hover:bg-[#f8c156]/10 transition-colors duration-300">
                    <span className="flex-shrink-0 w-8 h-8 bg-[#f8c156]/20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-[#f8c156]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </span>
                    <span className="text-[#455a73] leading-relaxed">Real-time system monitoring with auto-shutdown for anomalies</span>
                  </li>
                  <li className="flex items-start p-4 bg-[#f8c156]/5 rounded-lg group-hover:bg-[#f8c156]/10 transition-colors duration-300">
                    <span className="flex-shrink-0 w-8 h-8 bg-[#f8c156]/20 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-[#f8c156]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </span>
                    <span className="text-[#455a73] leading-relaxed">Electrical safety mechanisms to prevent overload</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Commitment to Safety */}
          <div className="bg-white rounded-xl shadow-lg p-8 border border-[#e8f5f2]">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="md:w-1/4 flex justify-center">
                <div className="w-24 h-24 rounded-full bg-[#38b5a0]/10 flex items-center justify-center">
                  <svg className="w-12 h-12 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                </div>
              </div>
              <div className="md:w-3/4">
                <h3 className="text-2xl font-bold mb-3 text-[#1a2942]">Our Commitment to Safety</h3>
                <p className="text-[#455a73] mb-4">
                  At EMS Pulse, safety isn't just a feature—it's our foundation. Our comprehensive training program ensures that all operators understand how to safely use the equipment and respond to any situation.
                </p>
                <p className="text-[#455a73]">
                  We provide regular system updates, maintenance checks, and safety refresher courses to keep all our partners and their clients protected.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scientific Research */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block bg-[#38b5a0]/10 text-[#38b5a0] text-sm font-semibold mb-3 py-1 px-3 rounded-full">
              EVIDENCE-BASED
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#1a2942]">Backed by Scientific Research</h2>
            <p className="text-[#455a73] max-w-3xl mx-auto text-lg">
              Our technology is supported by peer-reviewed research demonstrating the effectiveness and safety of EMS training
              </p>
            </div>
            
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-md p-8 border border-[#e8f5f2] hover:shadow-lg transition-all h-full">
              <h3 className="text-xl font-bold mb-6 text-[#1a2942]">Research Highlights</h3>
              <ul className="space-y-5">
                <li className="bg-[#f4fbfa] p-5 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-[#38b5a0]/10 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-[#1a2942] text-lg mb-1">Strength & Muscle Activation</p>
                      <p className="text-[#455a73] text-sm">Studies show up to <span className="text-[#38b5a0] font-semibold">40% greater muscle activation</span> compared to conventional resistance training.</p>
                    </div>
                  </div>
                </li>
                <li className="bg-[#fff6e5] p-5 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-[#f8c156]/10 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-[#f8c156]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-[#1a2942] text-lg mb-1">Time Efficiency</p>
                      <p className="text-[#455a73] text-sm">Research confirms <span className="text-[#f8c156] font-semibold">comparable results</span> to conventional training in just <span className="text-[#f8c156] font-semibold">20% of the time</span> investment.</p>
                    </div>
                  </div>
                </li>
                <li className="bg-[#e8f0ff] p-5 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-[#2389db]/10 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-[#2389db]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-[#1a2942] text-lg mb-1">Metabolic Benefits</p>
                      <p className="text-[#455a73] text-sm">Clinical studies demonstrate <span className="text-[#2389db] font-semibold">increased metabolic rate</span> and improved glucose metabolism.</p>
                    </div>
                  </div>
                </li>
                <li className="bg-gradient-to-r from-[#f4fbfa] to-[#e8f0ff] p-5 rounded-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-10 h-10 bg-white/80 rounded-full flex items-center justify-center mr-4">
                      <svg className="w-5 h-5 text-[#38b5a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-[#1a2942] text-lg mb-1">Rehabilitation Applications</p>
                      <p className="text-[#455a73] text-sm">Research supports the use of EMS for <span className="text-[#38b5a0] font-semibold">muscle recovery</span>, <span className="text-[#2389db] font-semibold">injury prevention</span>, and rehabilitation.</p>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl shadow-md p-8 border border-[#e8f5f2] hover:shadow-lg transition-all h-full">
              <h3 className="text-xl font-bold mb-6 text-[#1a2942]">Performance Statistics</h3>
              <p className="text-[#455a73] mb-8 text-sm">
                Our technology has demonstrated significant improvements across key performance metrics in controlled studies.
              </p>
              <div className="space-y-5">
                <div className="relative overflow-hidden p-5 bg-[#f4fbfa] rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#1a2942] font-semibold">Muscle Strength Gain</span>
                    <span className="text-[#38b5a0] font-bold text-2xl">+85%</span>
                  </div>
                  <div className="w-full h-2 bg-[#e8f5f2] rounded-full overflow-hidden">
                    <div className="w-[85%] h-full bg-[#38b5a0] rounded-full"></div>
                  </div>
                  <p className="text-sm text-[#455a73] mt-2">Compared to traditional training over 12 weeks</p>
                </div>

                <div className="relative overflow-hidden p-5 bg-[#fff6e5] rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#1a2942] font-semibold">Training Time Reduction</span>
                    <span className="text-[#f8c156] font-bold text-2xl">-80%</span>
                  </div>
                  <div className="w-full h-2 bg-[#ffecc6] rounded-full overflow-hidden">
                    <div className="w-[80%] h-full bg-[#f8c156] rounded-full"></div>
                  </div>
                  <p className="text-sm text-[#455a73] mt-2">Achieve similar results in just 20 minutes</p>
                </div>

                <div className="relative overflow-hidden p-5 bg-[#e8f0ff] rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#1a2942] font-semibold">Client Satisfaction</span>
                    <span className="text-[#2389db] font-bold text-2xl">98%</span>
                  </div>
                  <div className="w-full h-2 bg-[#d1e0ff] rounded-full overflow-hidden">
                    <div className="w-[98%] h-full bg-[#2389db] rounded-full"></div>
                  </div>
                  <p className="text-sm text-[#455a73] mt-2">Based on post-training satisfaction surveys</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-[#1a2942] text-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Experience Our Technology Firsthand</h2>
          <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
            Book a demonstration to see how our EMS systems can transform your fitness or rehabilitation business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/contact" className="inline-flex items-center justify-center px-8 py-3 rounded-lg bg-[#38b5a0] text-white font-medium hover:bg-[#2da08c] transition-colors">
              Schedule a Demo
            </Link>
            <Link href="/support-training" className="inline-flex items-center justify-center px-8 py-3 rounded-lg bg-white text-[#1a2942] font-medium hover:bg-slate-100 transition-colors">
              Learn About Training
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default TechnologySafetyPage; 